import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, PlusCircle, AlertTriangle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal para registrar un pago huérfano (dinero entró al banco pero NO existe
 * orden previa). Crea una orden sintética RECONCILIACION BANCARIA y postea
 * el crédito al ledger del comercio.
 */
export default function RegisterOrphanPaymentModal({ merchants: merchantsProp, onClose, onSuccess, setToast }) {
  const [merchants, setMerchants] = useState(merchantsProp || []);
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [merchantId, setMerchantId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [payerName, setPayerName] = useState('');
  const [paidAtDate, setPaidAtDate] = useState(new Date().toISOString().slice(0, 10));
  const [paidAtTime, setPaidAtTime] = useState('12:00');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch merchants si el prop viene vacío (ej. cuando el modal se abre desde
  // el tab "Ordenes" que no carga la lista de merchants).
  useEffect(() => {
    const eligible = (merchantsProp || []).filter(m => m.commission_model === 'daily_alliance');
    if (eligible.length > 0) { setMerchants(merchantsProp); return; }
    const loadMerchants = async () => {
      setLoadingMerchants(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BACKEND_URL}/api/super-admin/merchants`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMerchants(res.data || []);
      } catch (err) {
        setError('No se pudieron cargar los comercios.');
      } finally {
        setLoadingMerchants(false);
      }
    };
    loadMerchants();
  }, [merchantsProp]);

  // Filtrar solo merchants daily_alliance (los del ledger)
  const eligibleMerchants = (merchants || []).filter(m => m.commission_model === 'daily_alliance');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!merchantId) { setError('Selecciona el comercio.'); return; }
    if (!amount || parseFloat(amount) <= 0) { setError('El monto debe ser mayor a 0.'); return; }
    if (!paymentReference.trim()) { setError('La referencia bancaria es obligatoria.'); return; }

    const paidAtIso = new Date(`${paidAtDate}T${paidAtTime}:00Z`).toISOString();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/super-admin/orders/orphan-payment`,
        {
          merchant_id: merchantId,
          amount: parseFloat(amount),
          payment_reference: paymentReference.trim(),
          payer_name: payerName.trim() || undefined,
          paid_at: paidAtIso,
          notes: notes.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setToast?.({ type: 'success', message: `Pago huérfano registrado como ${res.data.order_code}` });
      onSuccess?.(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Error al registrar pago huérfano');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="orphan-payment-modal">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ backgroundColor: '#003594' }}>
          <div className="flex items-center gap-3 text-white">
            <PlusCircle className="w-5 h-5" />
            <div>
              <h3 className="text-lg font-bold">Registrar pago huérfano</h3>
              <p className="text-xs text-blue-200">Crea una orden sintética + asiento en el ledger</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-300" data-testid="orphan-payment-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-6 space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              Usar solo cuando el dinero entró al banco pero NO existe una orden previa. Crea una orden RECONCILIACION BANCARIA y acredita el monto al ledger del comercio.
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Comercio *</label>
            <select
              value={merchantId} onChange={(e) => setMerchantId(e.target.value)}
              data-testid="orphan-payment-merchant"
              disabled={loadingMerchants}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
            >
              <option value="">{loadingMerchants ? 'Cargando comercios...' : '-- Selecciona el comercio --'}</option>
              {eligibleMerchants.map(m => (
                <option key={m.user_id} value={m.user_id}>
                  {m.company_name || m.email} · {m.email}
                </option>
              ))}
            </select>
            {!loadingMerchants && eligibleMerchants.length === 0 && (
              <p className="text-xs text-slate-500 mt-1">No hay comercios en modelo daily_alliance.</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Referencia bancaria *</label>
            <input
              type="text" value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="Ej: 61605632046" data-testid="orphan-payment-reference"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 font-mono"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Monto (VES) *</label>
              <input
                type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                data-testid="orphan-payment-amount"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Payer (opcional)</label>
              <input
                type="text" value={payerName} onChange={(e) => setPayerName(e.target.value)}
                placeholder="RECONCILIACION BANCARIA" data-testid="orphan-payment-payer"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Fecha del pago</label>
              <input
                type="date" value={paidAtDate} onChange={(e) => setPaidAtDate(e.target.value)}
                data-testid="orphan-payment-date"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Hora (UTC)</label>
              <input
                type="time" value={paidAtTime} onChange={(e) => setPaidAtTime(e.target.value)}
                data-testid="orphan-payment-time"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Notas (opcional)</label>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="Ej: pago huérfano identificado en revisión de estado de cuenta bancario" data-testid="orphan-payment-notes"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-start gap-2" data-testid="orphan-payment-error">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-slate-50" data-testid="orphan-payment-cancel">
              Cancelar
            </button>
            <button
              type="submit" disabled={submitting || eligibleMerchants.length === 0} data-testid="orphan-payment-submit"
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Registrando...</> : <><PlusCircle className="w-4 h-4" /> Registrar</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
